import { forwardRef } from 'react';
import clsx from 'clsx';

import { ButtonWrapper } from '../ButtonWrapper';
import { Spinner } from '../../spinner/Spinner';

import type { ButtonVariant } from '../types';
import { resolveVariantClasses } from '../utils';
import { twMerge } from 'tailwind-merge';

export type ButtonProps = {
  className?: string;
  children: React.ReactNode;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  variant?: ButtonVariant;
  loading?: boolean;
  Icon?: (props: React.SVGProps<SVGSVGElement>) => JSX.Element; //svg
  iconPlacement?: 'left' | 'right';
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

export const Button = forwardRef(function Button(
  {
    className = '',
    children,
    size = 'md',
    variant = 'primary',
    loading,
    Icon,
    iconPlacement,
    ...rest
  }: ButtonProps,
  ref: React.ForwardedRef<HTMLAnchorElement | HTMLButtonElement>
) {
  return (
    <ButtonWrapper
      aria-busy="false"
      className={clsx(
        `relative`,
        `inline-flex items-center`,
        `font-medium`,
        {
          'rounded-sm border border-solid':
            variant !== 'link' && variant !== 'rounded',
          'cap-xs': size === 'xs',
          'text-sm': size === 'sm' || size === 'md',
          'cap-base': size === 'lg',
          'py-2 px-2.5': size === 'xs' && variant !== 'link',
          'py-1 px-2': size === 'sm' && variant !== 'link',
          'py-2 px-4': size === 'md' && variant !== 'link',
          'py-4 px-4': size === 'lg' && variant !== 'link',
          'p-0 underline': variant === 'link',
        },
        resolveVariantClasses(variant),
        `disabled:opacity-25`,
        className
      )}
      {...rest}
    >
      {typeof Icon !== 'undefined' && iconPlacement === 'left' && (
        <Icon className={clsx('h-4 w-4', 'mr-2 -ml-0.5')} aria-hidden="true" />
      )}
      {children}
      {typeof Icon !== 'undefined' && iconPlacement === 'right' && (
        <Icon className={clsx('h-4 w-4', 'ml-2 -mr-0.5')} aria-hidden="true" />
      )}
      {loading && (
        <div
          className={twMerge(
            clsx(
              'absolute top-0 left-0',
              'h-full w-full',
              'flex justify-center items-center',
              'bg-primary-darken-20',
              'rounded-sm',
              resolveVariantClasses(variant)
            )
          )}
        >
          <Spinner size="sm" />
        </div>
      )}
    </ButtonWrapper>
  );
});

export default Button;
